<script setup>
/**
 * The only single-line text control in the Waslo tree. THE PLAN, §5 R7.
 *
 * Same reason as `WasloTextArea`: `dir="auto"` has to be on the `<input>` itself, and
 * Chatwoot's `components-next/input/Input.vue` does not forward it there. Everything
 * else — `h-10`, `rounded-lg`, `bg-n-alpha-black2`, `outline-offset-[-1px]`, the label
 * and message treatment — is copied from that file so the two are indistinguishable
 * standing next to each other, minus the `dark:` duplicates the contract forbids (R4).
 *
 * The resting outline and the hint-above-the-field follow `WasloTextArea`, for the reason
 * written out in full there: `n-weak` is a divider token, and a control whose only edge
 * appears on focus reads as a printed value rather than something you can change.
 */
import { computed, getCurrentInstance, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { formatCount } from '../helpers/plural.js';

const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  type: { type: String, default: 'text' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  /** What to write in the box, above the box. See `WasloTextArea`. */
  hint: { type: String, default: '' },
  message: { type: String, default: '' },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error', 'success'].includes(value),
  },
  disabled: { type: Boolean, default: false },
  min: { type: [String, Number], default: undefined },
  max: { type: [String, Number], default: undefined },
  step: { type: [String, Number], default: undefined },
  /**
   * The ceiling, counted out loud — `WasloTextArea`'s prop, same name, same two jobs, because a
   * one-line box is where most of a WhatsApp template's limits actually live (a button label is
   * 25 characters, a footer 60) and the multi-line box was the only control in the tree that
   * could say so.
   *
   * It is a HARD cap as well as a readout: the 26th character of a button label is one Meta
   * refuses, so there is nothing to be gained by accepting it and a review cycle to be lost.
   * A value that arrives over the ceiling from somewhere this box did not type — a template
   * written in WhatsApp Manager and read back — is not truncated by `maxlength`, and the count
   * is drawn in ruby so it is read rather than assumed.
   */
  maxLength: { type: Number, default: 0 },
});

const emit = defineEmits(['update:modelValue', 'blur']);

const fieldId = `waslo-input-${getCurrentInstance().uid}`;
const isFocused = ref(false);

const messageClass = computed(() =>
  props.messageType === 'error' ? 'text-n-ruby-9' : 'text-n-slate-11'
);

const characterCount = computed(() => String(props.modelValue ?? '').length);

// "17 / 200" in the screen's digits (O-5): Arabic-Indic on an Arabic screen. A readout, so
// no grouping, exactly as it read before.
const { locale } = useI18n();
const countLabel = computed(
  () =>
    `${formatCount(characterCount.value, locale.value, { useGrouping: false })} / ${formatCount(props.maxLength, locale.value, { useGrouping: false })}`
);

const countClass = computed(() =>
  props.maxLength && characterCount.value > props.maxLength
    ? 'text-n-ruby-9'
    : 'text-n-slate-10'
);

// `message` has to be non-empty, not just `messageType === 'error'` — the same rule
// `WasloTextArea` already writes down, and the same bug it already fixed. A caller that
// declares "when this field has something to say, it is an error" and has nothing to say
// yet is the ordinary resting case; reading the type alone painted an untouched, empty
// field ruby on first paint, which was visible the moment a form used this control
// outside an Accordion.
const outlineClass = computed(() =>
  props.messageType === 'error' && props.message
    ? 'outline-n-ruby-8 hover:outline-n-ruby-9'
    : 'outline-n-slate-7 hover:outline-n-slate-8 focus:outline-n-brand'
);

// A number field emits a number, never the string the DOM hands us — the BFF rejects
// "0.5" with a 422 and the owner would see a validation error they cannot act on.
const onInput = event => {
  const raw = event.target.value;
  if (props.type === 'number') {
    emit('update:modelValue', raw === '' ? '' : Number(raw));
    return;
  }
  emit('update:modelValue', raw);
};
</script>

<template>
  <div class="relative flex flex-col min-w-0">
    <!--
      The count rides on the LABEL row rather than under the box, which is where
      `WasloTextArea` puts it. A one-line control is 40px tall and its edge is an outline on the
      element itself; a counter below it would sit between the box and its own error sentence,
      which is the one place on this control nothing may go.
    -->
    <div
      v-if="label || maxLength"
      class="flex items-baseline justify-between min-w-0 gap-4 mb-1"
    >
      <label
        v-if="label"
        :for="fieldId"
        class="text-sm font-medium text-n-slate-12"
      >
        {{ label }}
      </label>
      <span
        v-if="maxLength"
        class="text-xs tabular-nums shrink-0"
        :class="countClass"
        data-waslo-input-count
      >
        {{ countLabel }}
      </span>
    </div>
    <p v-if="hint" class="min-w-0 mb-2 text-sm text-n-slate-11">
      {{ hint }}
    </p>
    <!-- The one sanctioned raw input. `waslo-lint` exempts this file by name; see R7. -->
    <input
      :id="fieldId"
      dir="auto"
      :value="modelValue"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      :min="min"
      :max="max"
      :step="step"
      :maxlength="maxLength || undefined"
      :class="[outlineClass, { focus: isFocused }]"
      class="block w-full h-10 !px-3 !py-2.5 text-sm reset-base !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      @input="onInput"
      @focus="isFocused = true"
      @blur="
        isFocused = false;
        emit('blur', $event);
      "
    />
    <p v-if="message" class="min-w-0 mt-2 mb-0 text-sm" :class="messageClass">
      {{ message }}
    </p>
  </div>
</template>
